import { getDatabase, ref, get, push, update } from 'firebase/database';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { requireAdmin } from './adminGuard';
import { recordActivity } from './activityApi';
import { ServiceLogRef, serviceLogPath, serviceLogValueError } from '../types/serviceLogs';

/**
 * Corrects the value of a logged service (admin only). Reads the stored value
 * fresh so `from` is what was actually replaced, then writes the new value and
 * the correction entry in one update.
 */
export const correctServiceLogValue = async (
    logRef: ServiceLogRef,
    newValue: number,
    reason?: string,
): Promise<void> => {
    const db = getDatabase();
    const uid = await requireAdmin(db, 'korrigera värden');
    const path = serviceLogPath(logRef);

    const snapshot = await get(ref(db, `${path}/value`));
    const current: unknown = snapshot.val();
    if (typeof current !== 'number' || !Number.isFinite(current)) {
        throw new Error('Loggposten saknar ett värde att korrigera');
    }
    const invalid = serviceLogValueError(newValue, current);
    if (invalid) throw new Error(invalid);

    const correctionKey = push(ref(db, `${path}/corrections`)).key;
    if (!correctionKey) throw new Error('Kunde inte skapa korrigeringen');

    const trimmedReason = reason?.trim();
    await update(ref(db, path), {
        value: newValue,
        [`corrections/${correctionKey}`]: {
            from: current,
            to: newValue,
            changedBy: uid,
            changedWhen: formatDateForStorage(new Date()),
            ...(trimmedReason ? { reason: trimmedReason } : {}),
        },
    });
    recordActivity('serviceLog.updated', { buildingKey: logRef.buildingKey, targetKey: logRef.logId });
};
